'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Card from '@/components/card/Card';
import { RelatedCollectionProps } from './types';
import { fetchRelatedCollection } from '@/api/content-hub';
import { useTranslations } from '@/lib';
import type { LocaleLanguage } from '@/lib/locale-context';
import { CardProps, CTACardProps } from '../card/types';
import { Headline } from '../headline/Headline';

export default function RelatedCollection({
  categories,
  limit,
  type,
  enableCtaCard,
  title,
  links,
  description,
  theme = 'dark',
  slug,
}: RelatedCollectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [relatedCollection, setRelatedCollection] = useState<CardProps[]>([]);
  const context = useTranslations();

  const categoryTitles = useMemo(
    () => categories?.map((category) => category.title),
    [categories],
  );

  // LOAD DATA
  useEffect(() => {
    const loadData = async () => {
      try {
        const items = await fetchRelatedCollection(
          slug ? slug : null,
          type,
          context?.locale as LocaleLanguage,
          categoryTitles,
          limit,
        );

        let result: any[] = items || [];
        if (enableCtaCard) {
          const cta: CTACardProps = {
            variant: 'cta',
            links,
            title,
            description,
            theme: 'dark',
          };
          result = [...result, cta];
        }

        setRelatedCollection(result);
      } catch (e) {
        console.error('Failed to fetch related collection:', e);
      }
    };

    loadData();
  }, [
    categoryTitles,
    context?.locale,
    description,
    enableCtaCard,
    limit,
    links,
    slug,
    title,
    type,
  ]);

  //  sync dots with scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);
        if (visible && visible.target) {
          const index = itemRefs.current.indexOf(
            visible.target as HTMLDivElement,
          );
          if (index !== -1) setActiveIndex(index);
        }
      },
      {
        root: scrollRef.current,
        threshold: 0.6, // item must be 60% visible
      },
    );

    itemRefs.current.forEach((ref) => ref && observer.observe(ref));

    return () => observer.disconnect();
  }, [relatedCollection.length]);

  // HANDLE DOT CLICK scrollTo item
  const goTo = (index: number) => {
    setActiveIndex(index);
    const el = itemRefs.current[index];
    if (el && scrollRef.current) {
      scrollRef.current.scrollTo({
        left: el.offsetLeft,
        behavior: 'smooth',
      });
    }
  };
  if (!relatedCollection.length) {
    return null;
  }
  return (
    <section
      className={`related-collection ${theme === 'dark' ? 'bg-brand-900 py-12' : 'bg-white py-5'}`}
    >
      <div className="container mx-auto gap-6">
        <Headline
          level="h3"
          variant="headingThree"
          className={`col-span-full ${theme === 'dark' ? 'text-white' : 'text-[#07314C]'}`}
          title={
            context?.locale !== 'de' ? 'Related Content' : 'Verwandte Inhalte'
          }
        />

        {/* Slider until md-; grid on md+ */}
        <div
          ref={scrollRef}
          className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 md:grid md:overflow-visible md:pb-0"
          style={{
            gridTemplateColumns: `repeat(${relatedCollection.length === 1 ? 3 : relatedCollection.length}, minmax(0, 1fr))`,
          }}
        >
          {relatedCollection.map((collection: CardProps, i: number) => (
            <div
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              key={i}
              className={`${relatedCollection.length > 1 ? 'w-3/4 md:w-full' : 'w-full'} flex-shrink-0 snap-center md:flex-shrink`}
            >
              <Card
                {...collection}
                className="truncate-description-related-card h-full"
              />
            </div>
          ))}
        </div>

        {/* Pagination dots */}
        <div className="mt-4 flex justify-center gap-2 md:hidden">
          {relatedCollection.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`h-2 w-2 rounded-full transition-all ${
                activeIndex === i ? 'bg-gray-800' : 'bg-gray-300'
              }`}
              aria-label={`Go to card ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
